/**
 * The copy buttons on a sentence's own page, and on the 例句 in an article.
 *
 * A 例句 page is five writings of one sentence — the characters in both
 * scripts, both readings, bopomofo, and the translation — and it exists so that
 * a learner can take whichever of them they need. Selecting one by dragging
 * across it is the part that is genuinely hard on a phone, which is where these
 * pages are read, so each line carries a button that does it.
 *
 * A grammar article shows the same sentence as a block of three lines, and has
 * one button for the block rather than one for each line: the sentence is being
 * read as an example in the middle of an explanation rather than looked up, and
 * what a learner takes from it is the whole example. That button copies the
 * three lines as they are read, one to a line, so the reading stays under the
 * characters and the translation under the reading.
 *
 * Plain text and nothing else. `src/tools` puts a coloured `text/html` flavour
 * on the clipboard beside the text, because what it is copying is a tone-
 * coloured conversion and the colours are the point of it. Here the point is
 * the sentence, and this is in the bundle every page on the site loads, so it
 * stays as small as the job is.
 *
 * What gets copied is read out of the lines at the moment of the click, rather
 * than handed to the button up front. The sentence never changes, so this buys
 * nothing today; what it buys is that there is one copy of the text on the page
 * rather than a second one in an attribute that could disagree with it.
 */

/** How long a button holds its confirmation before going back to normal. */
const feedbackMilliseconds = 2000;

/** How a copy attempt ended. */
type CopyOutcome = "copied" | "failed";

/** What a screen reader is told for each outcome. */
const messages: Readonly<Record<CopyOutcome, string>> = {
  copied: "Copied",
  failed: "Could not copy",
};

/** The class a button wears for each outcome, for the two seconds it lasts. */
const outcomeClasses: Readonly<Record<CopyOutcome, string>> = {
  copied: "liju-copied",
  failed: "liju-copy-failed",
};

/**
 * Wire up every copy button under `root`, and show them.
 *
 * The buttons are rendered hidden by `partials/liju-copy-button.html` and shown
 * here, so a visitor whose browser did not run this — an old one, a blocked
 * bundle, JavaScript turned off — is shown nothing rather than five buttons
 * that do nothing when they are pressed.
 *
 * A button with nothing beside it to copy is left hidden rather than wired to
 * nothing. That cannot happen from the templates as they stand; it is the state
 * that would otherwise put an empty string on somebody's clipboard.
 */
export function startLijuCopyButtons(root: ParentNode): void {
  const buttons = root.querySelectorAll<HTMLButtonElement>("button.liju-copy");

  for (const button of buttons) {
    const lines = copiedLines(button);

    if (lines.length === 0) {
      continue;
    }

    wireLijuCopyButton(button, lines);
    button.hidden = false;
  }
}

/**
 * The lines one button copies, in the order they are read.
 *
 * The button says which it is. A block button in an article takes every writing
 * of the sentence the article shows, which is the ones that are not hidden: the
 * block carries 繁體, the 國語 reading and bopomofo as well, hidden until there
 * is something for a reader to switch between them with, and a learner copying
 * an example has not asked for five lines of it. Any other button takes the one
 * line it sits beside.
 */
function copiedLines(button: HTMLButtonElement): Element[] {
  const beside = button.parentElement;

  if (beside === null) {
    return [];
  }

  if (button.classList.contains("liju-block-copy")) {
    return [...beside.querySelectorAll(".liju-element:not([hidden])")];
  }

  const line = beside.querySelector(".liju-line-text");

  return line === null ? [] : [line];
}

/**
 * Wire one button to the lines it sits beside.
 *
 * The confirmation is a class the stylesheet turns into a tick, and a live
 * region for a screen reader, which is the same news told twice because a tick
 * is not announced and an announcement is not seen. Both are cleared together,
 * and a second click restarts the countdown rather than letting the first
 * click's timer cut the second one short.
 */
export function wireLijuCopyButton(
  button: HTMLButtonElement,
  lines: readonly Element[],
): void {
  const said = button.querySelector(".liju-copy-said");
  let restore: ReturnType<typeof setTimeout> | undefined;

  button.addEventListener("click", () => {
    void copyText(textOf(lines)).then((outcome) => {
      button.classList.add(outcomeClasses[outcome]);

      if (said !== null) {
        said.textContent = messages[outcome];
      }

      if (restore !== undefined) {
        clearTimeout(restore);
      }

      restore = setTimeout(() => {
        button.classList.remove(outcomeClasses.copied, outcomeClasses.failed);

        if (said !== null) {
          said.textContent = "";
        }

        restore = undefined;
      }, feedbackMilliseconds);
    });
  });
}

/**
 * What the lines say, one to a line.
 *
 * A newline between them rather than a space, because these are three writings
 * of one sentence and not three sentences: pasted into a note or a flashcard,
 * the characters, the reading and the translation each want a line of their
 * own. A line with nothing in it is left out rather than pasted as a blank one.
 */
function textOf(lines: readonly Element[]): string {
  return lines
    .map((line) => line.textContent.trim())
    .filter((text) => text !== "")
    .join("\n");
}

/**
 * Put the text on the clipboard, and say whether it went.
 *
 * Everything the clipboard can refuse lands on `failed`: a browser with no
 * clipboard API, a page without permission for it, and a click the browser did
 * not count as a gesture. None of them is a reason to throw, and the button has
 * something honest to say about all three.
 */
async function copyText(text: string): Promise<CopyOutcome> {
  const clipboard = navigator.clipboard as Clipboard | undefined;

  if (clipboard === undefined) {
    return "failed";
  }

  try {
    await clipboard.writeText(text);
    return "copied";
  } catch {
    return "failed";
  }
}
